/**
 * Rich message payloads (cards, carousels, chips) as stored on drafts and sent
 * messages. Browser-safe so the inbox can render what the customer saw.
 */
import { Option, Schema } from "effect";

const RcsMediaSchema = Schema.Struct({
  fileUrl: Schema.String,
  thumbnailUrl: Schema.optional(Schema.String),
  height: Schema.optional(Schema.Literal("SHORT", "MEDIUM", "TALL")),
});

const RcsSuggestionSchema = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("reply"),
    text: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("openUrl"),
    text: Schema.String,
    url: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("dial"),
    text: Schema.String,
    phoneNumber: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("viewLocation"),
    text: Schema.String,
    latitude: Schema.Number,
    longitude: Schema.Number,
    label: Schema.optional(Schema.String),
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("shareLocation"),
    text: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("calendarEvent"),
    text: Schema.String,
    startTime: Schema.String,
    endTime: Schema.String,
    title: Schema.String,
    description: Schema.optional(Schema.String),
    postbackData: Schema.optional(Schema.String),
  }),
);

const RcsCardSchema = Schema.Struct({
  title: Schema.optional(Schema.String),
  description: Schema.optional(Schema.String),
  media: Schema.optional(RcsMediaSchema),
  suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
});

/**
 * An RCS agent message body; mirrors `RcsMessageContent` in
 * redo-communication-common, which can't be imported here without a cycle.
 * Decodes untrusted stored JSON, so a writer picks the tagged `kind` openly.
 */
export const RcsMessageContentSchema = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("text"),
    text: Schema.String,
    suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
  }),
  Schema.Struct({
    kind: Schema.Literal("media"),
    media: RcsMediaSchema,
    suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
  }),
  Schema.Struct({
    kind: Schema.Literal("cards"),
    cards: Schema.NonEmptyArray(RcsCardSchema),
    orientation: Schema.optional(Schema.Literal("VERTICAL", "HORIZONTAL")),
    thumbnailAlignment: Schema.optional(Schema.Literal("LEFT", "RIGHT")),
    cardWidth: Schema.optional(Schema.Literal("SMALL", "MEDIUM")),
  }),
);
export type RcsMessageContentData = typeof RcsMessageContentSchema.Type;

/**
 * A draft's choices as a list the customer picks from: sections, subtitles,
 * several picks per section, and product images. Written by the agent's
 * offer-choices tool on a channel with lists; Apple Messages for Business
 * sends it as a List Picker. Decodes untrusted stored JSON.
 */
export const ChoiceListContentSchema = Schema.Struct({
  kind: Schema.Literal("choiceList"),
  text: Schema.String,
  sections: Schema.NonEmptyArray(
    Schema.Struct({
      title: Schema.String,
      multipleSelection: Schema.Boolean,
      items: Schema.NonEmptyArray(
        Schema.Struct({
          title: Schema.String,
          subtitle: Schema.optional(Schema.String),
          /** A catalog product's image, never a URL the agent wrote. */
          imageUrl: Schema.optional(Schema.String),
        }),
      ),
    }),
  ),
});
export type ChoiceListContent = typeof ChoiceListContentSchema.Type;

/** A stored or produced choice list, or null when it isn't one. */
export function decodeChoiceListContent(
  raw: unknown,
): ChoiceListContent | null {
  return Option.getOrNull(
    Schema.decodeUnknownOption(ChoiceListContentSchema)(raw),
  );
}

/**
 * A draft's question with times to pick one from, for an event at a place:
 * the agent's drop-off plan, which Apple Messages for Business sends as a
 * Time Picker. Decodes untrusted stored JSON.
 */
export const TimePickerContentSchema = Schema.Struct({
  kind: Schema.Literal("timePicker"),
  text: Schema.String,
  event: Schema.Struct({
    title: Schema.String,
    location: Schema.Struct({
      title: Schema.String,
      latitude: Schema.optional(Schema.Number),
      longitude: Schema.optional(Schema.Number),
    }),
    /** IANA zone of the location; the slots show in it. */
    timeZone: Schema.String,
    /** An Apple Maps link to the location, sent as a map card. */
    mapUrl: Schema.optional(Schema.String),
    slots: Schema.NonEmptyArray(
      Schema.Struct({
        /** Local wall time, "2026-10-30T14:00". */
        identifier: Schema.String,
        title: Schema.String,
        /** ISO instant. */
        start: Schema.String,
        durationMinutes: Schema.Number,
      }),
    ),
  }),
});
export type TimePickerContent = typeof TimePickerContentSchema.Type;

const ApplePayItemSchema = Schema.Struct({
  label: Schema.String,
  /** Decimal text, "12.00". */
  amount: Schema.String,
});

const ApplePayContactFieldSchema = Schema.Literal(
  "email",
  "name",
  "phone",
  "phoneticName",
  "post",
);

/** A draft's sentence framing an amount to pay, which Apple Messages for
 * Business sends as an Apple Pay request. Decodes untrusted stored JSON. */
export const ApplePayRequestContentSchema = Schema.Struct({
  kind: Schema.Literal("applePayRequest"),
  text: Schema.String,
  /** The brand's name on the payment sheet, 64 characters at most. */
  merchantName: Schema.String,
  countryCode: Schema.String,
  currencyCode: Schema.String,
  lineItems: Schema.NonEmptyArray(ApplePayItemSchema),
  total: ApplePayItemSchema,
  shippingMethods: Schema.optional(
    Schema.Array(
      Schema.Struct({
        ...ApplePayItemSchema.fields,
        identifier: Schema.String,
        detail: Schema.String,
      }),
    ),
  ),
  requiredBillingContactFields: Schema.optional(
    Schema.Array(ApplePayContactFieldSchema),
  ),
  requiredShippingContactFields: Schema.optional(
    Schema.Array(ApplePayContactFieldSchema),
  ),
});
export type ApplePayRequestContent = typeof ApplePayRequestContentSchema.Type;

/**
 * What a sent message's `message_sms.rich_content` holds. Tagged by `format`
 * so each channel keeps its own shape: Apple Messages for Business adds an
 * `apple_messages` member here rather than squeezing into the RCS one.
 */
export const MessageRichContent = Schema.Union(
  Schema.Struct({
    format: Schema.Literal("rcs"),
    content: RcsMessageContentSchema,
  }),
);
export type MessageRichContent = typeof MessageRichContent.Type;

/**
 * Read-side decode: a payload this build doesn't know (a newer format, a
 * malformed row) becomes null so the message falls back to its text instead of
 * failing the whole transcript.
 */
export const StoredMessageRichContent = Schema.transform(
  Schema.Unknown,
  Schema.NullOr(Schema.typeSchema(MessageRichContent)),
  {
    strict: true,
    decode: (raw) =>
      Option.getOrNull(Schema.decodeUnknownOption(MessageRichContent)(raw)),
    encode: (content) => content,
  },
);
